// Direct construction of the three Supabase sub-clients we actually use
// (auth-js / postgrest-js / functions-js). Skips `@supabase/supabase-js`'s
// `createClient` wrapper, which also constructs RealtimeClient + StorageClient
// + a fetchWithAuth proxy + listenForAuthEvents wiring (~6 kB brotli on top
// of the realtime/storage stubs already aliased in vite.config.ts). We keep
// the same `supabase.auth.X` / `supabase.from(...)` / `supabase.functions.invoke(...)`
// surface so every existing caller keeps working unchanged. See
// steam-port/docs/HOUSEKEEPING-supabase-direct-construction.md for the full
// before/after.
import { AuthClient, type GoTrueClient } from '@supabase/auth-js';
import { PostgrestClient } from '@supabase/postgrest-js';
import { FunctionsClient } from '@supabase/functions-js';
import { getDeviceHint } from './device-hint';
import { solveBits } from './pow-solver';
import {
  getTurnstileToken,
  TURNSTILE_STUB_TOKEN,
  TURNSTILE_OUTAGE_TOKEN,
} from './turnstile-client';

// AUTH_PAYMENT_HARDENING_PLAN.md §7.4.a — route Supabase auth through Vercel
// so the edge middleware's `rl:anon:ip:` Tier-A limiter has something to
// limit. `VITE_SUPABASE_URL` still points at `<ref>.supabase.co` for
// `functions/v1/*` and the DB / REST API; only `/auth/v1/*` is rerouted.
//
// IMPLEMENTATION NOTE (see docs/auth-hardening/T-26-anon-signup-client.md
// "Ambiguity notes"): supabase-js v2.103 does NOT expose an `auth.url`
// option on `SupabaseClientOptions`; the auth client URL is derived from
// the positional `supabaseUrl` argument in the `createClient` constructor
// (see node_modules/@supabase/supabase-js/dist/index.mjs constructor —
// `this.authUrl = new URL("auth/v1", baseUrl)`). To honor §7.4.a "auth URL
// points at `${window.location.origin}/auth/v1`" without also rerouting
// `rest/v1`, `storage/v1`, and `functions/v1`, we intercept at the
// `global.fetch` layer: requests whose URL prefix matches
// `${VITE_SUPABASE_URL}/auth/v1/` are rewritten to
// `${window.location.origin}/auth/v1/` before being dispatched. Every
// other Supabase URL (REST / storage / functions.invoke) passes through
// unchanged, matching the §7.4.a final paragraph requirement that
// `supabase.functions.invoke(...)` still hits `<ref>.supabase.co`.
// The plan's `auth: { url: ... }` literal is ALSO set via a typed helper
// for forward-compatibility with a future supabase-js release that does
// honor it (dropping the fetch rewrite then becomes a one-line change).

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Compute the in-browser Vercel-origin auth base URL. Falls back to the
 * Supabase direct URL on the server (SSR / tests) so Supabase's own URL
 * validation still resolves.
 */
function authBaseUrl(): string {
  return typeof window !== 'undefined'
    ? `${window.location.origin}/auth/v1`
    : `${SUPABASE_URL}/auth/v1`;
}

/**
 * Build a `fetch` wrapper that rewrites `${SUPABASE_URL}/auth/v1/*` →
 * `${authBaseUrl()}/*`. Pure URL rewrite — preserves method, headers, body,
 * credentials, and the returned Response.
 */
function makeAuthRewriteFetch(): typeof fetch {
  const supabaseAuthPrefix = `${SUPABASE_URL}/auth/v1/`;
  return (input, init) => {
    const originalUrl =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : input instanceof Request
            ? input.url
            : String(input);
    if (originalUrl.startsWith(supabaseAuthPrefix)) {
      const rewritten =
        authBaseUrl() + '/' + originalUrl.slice(supabaseAuthPrefix.length);
      if (input instanceof Request) {
        // Preserve method + headers + body from the Request by cloning.
        return fetch(new Request(rewritten, input), init);
      }
      return fetch(rewritten, init);
    }
    return fetch(input, init);
  };
}

/**
 * Mirror of supabase-js's default X-Client-Info header — kept identical
 * so server-side analytics + rate-limiter buckets see the same string the
 * old client emitted. Bumping supabase-js no longer auto-bumps this; if the
 * new version stamps a different value the change is intentional and
 * server-side allow-lists must be updated alongside.
 */
const DEFAULT_HEADERS = { 'X-Client-Info': 'supabase-js-web/2.103.0' };

/**
 * Build a session-token store key that matches what supabase-js's
 * `createClient` would have used (`sb-${hostname-first-label}-auth-token`).
 * Persisted sessions written by the old client remain readable after
 * the swap so live users do not get bounced to fresh-signup.
 */
function defaultStorageKey(url: string): string {
  try {
    return `sb-${new URL(url).hostname.split('.')[0]}-auth-token`;
  } catch {
    return 'sb-auth-token';
  }
}

/**
 * Build the trio of Supabase sub-clients (auth + rest + functions) and
 * wire `onAuthStateChange` to keep REST + functions Authorization headers
 * in sync with the active session — what `createClient`'s
 * `_listenForAuthEvents` / `fetchWithAuth` wrapper does internally.
 *
 * A custom fetch can be supplied: it is composed under the auth client
 * (so the §7.4.a /auth/v1/* rewrite still applies) AND under the REST +
 * functions clients (so any future per-request header injection threads
 * through both surfaces).
 */
type SupabaseLite = {
  auth: GoTrueClient;
  rest: PostgrestClient;
  functions: FunctionsClient;
  from: PostgrestClient['from'];
};

function buildSupabaseLite(customFetch: typeof fetch): SupabaseLite {
  const baseUrl = SUPABASE_URL.replace(/\/+$/, '');

  // The auth client carries the `apikey + Authorization: Bearer anonKey`
  // pair as defaults; supabase-js's `createClient` does the same in
  // `_initSupabaseAuthClient` (index.mjs:531). Without the `apikey` header
  // the Vercel auth-route middleware rejects the request as unsigned.
  const authHeaders: Record<string, string> = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  };

  const auth = new AuthClient({
    url: authBaseUrl(),
    headers: { ...DEFAULT_HEADERS, ...authHeaders },
    storageKey: defaultStorageKey(SUPABASE_URL),
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'implicit',
    fetch: customFetch,
    hasCustomAuthorizationHeader: false,
  });

  // REST + functions both want `apikey` + `Authorization: Bearer <jwt>`.
  // Until `onAuthStateChange` fires we seed the anon key as the bearer so
  // unauthenticated reads (`leaderboard_*_v` views) work before sign-in.
  const restHeaders: Record<string, string> = {
    ...DEFAULT_HEADERS,
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  };

  const rest = new PostgrestClient(`${baseUrl}/rest/v1`, {
    headers: restHeaders,
    fetch: customFetch,
    schema: 'public',
  });

  const functions = new FunctionsClient(`${baseUrl}/functions/v1`, {
    headers: { ...DEFAULT_HEADERS, apikey: SUPABASE_ANON_KEY },
    customFetch,
  });

  // Mirror supabase-js's `_handleTokenChanged`: when the auth client
  // refreshes / signs in / signs out, propagate the new access token into
  // the REST client's persistent headers and the functions client's
  // `Authorization`. Without this, RLS-protected REST reads would still
  // carry the anon JWT after sign-in.
  auth.onAuthStateChange((_event, session) => {
    const token = session?.access_token ?? SUPABASE_ANON_KEY;
    // PostgrestClient stores headers as `Headers` at the class level but
    // the `from()` builder reads `this.headers` and clones it per request,
    // so updating the live instance is enough.
    if (rest.headers instanceof Headers) {
      rest.headers.set('Authorization', `Bearer ${token}`);
    } else {
      (rest.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    functions.setAuth(token);
  });

  return {
    auth,
    rest,
    functions,
    from: rest.from.bind(rest) as PostgrestClient['from'],
  };
}

const supabase = buildSupabaseLite(makeAuthRewriteFetch());

export { supabase };

export interface RecoveryState {
  needed: boolean;
  hasEmail: boolean;
}

// Module-local state: nonce from the most-recent PoW `issue` round. Consumed
// by the bind POST after `signInAnonymously` resolves (§9.2 M-ARCH-11). Null
// outside an in-flight signup.
let _lastSignupNonce: string | null = null;

// Module-level flag guaranteeing the visibilitychange listener registers
// exactly once, even if `initAuth` is invoked multiple times (T-29 / §6.5).
let _visibilityListenerInstalled = false;

/**
 * Register a single `visibilitychange` listener that refreshes the Supabase
 * session when the tab becomes visible. Idempotent via
 * `_visibilityListenerInstalled` — a second call is a no-op.
 *
 * On visibility, after refreshing the session, also kicks `retryPendingBind`
 * so a bind attempt stranded by an offline/hidden tab can proceed
 * (§6.5 + T-26 wiring).
 */
function installVisibilityRefreshListener(): void {
  if (_visibilityListenerInstalled) return;
  if (typeof document === 'undefined') return;
  _visibilityListenerInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void supabase.auth
        .getSession()
        .then(({ data }) => {
          if (data.session) return supabase.auth.refreshSession();
          return undefined;
        })
        .finally(() => {
          void retryPendingBind();
        });
    }
  });
}

/**
 * Check whether the user needs session recovery.
 *
 * V3 §5.1 — expanded trigger conditions:
 *   1. `rc_session` cookie present but no Supabase session (existing).
 *   2. Supabase session present but server reports merged_into != null.
 *   3. URL contains ?recover=1 (magic-link landing).
 *   4. `rc_recovery_pending` localStorage key set by a prior start.
 *
 * `hasEmail` is a best-effort HINT only; correctness never depends on it.
 * Silent-recovery reads the HMAC-signed `rc_session` cookie on the SERVER
 * via /recovery/silent — NOT localStorage (Security Top-Blocker #4).
 */
/** True when the page URL is a recovery deep link (`?recover=1` or a magic-link `?t=`). */
export function hasRecoveryDeepLink(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('recover') === '1' || !!params.get('t');
  } catch {
    return false;
  }
}

export async function checkRecoveryNeeded(): Promise<RecoveryState> {
  const hasSessionCookie = typeof document !== 'undefined'
    ? document.cookie.split(';').some((c) => c.trim().startsWith('rc_session='))
    : false;
  const { data: session } = await supabase.auth.getSession();

  // Trigger 3: deep-link ?recover=1 / magic-link ?t=.
  if (hasRecoveryDeepLink()) {
    return { needed: true, hasEmail: false };
  }

  // Trigger 4: a prior start-recovery flagged the UI as pending.
  if (typeof localStorage !== 'undefined') {
    try {
      if (localStorage.getItem('rc_recovery_pending') === '1') {
        return { needed: true, hasEmail: false };
      }
    } catch { /* quota / private mode */ }
  }

  // Trigger 1: cookie + no session.
  if (hasSessionCookie && !session?.session) {
    return { needed: true, hasEmail: false };
  }

  // Trigger 2: session is for a merged-loser uid. Check payment-status
  // (cheap, already the status poller's home) — on 409 merged, we know.
  if (session?.session?.user?.id) {
    try {
      const { data: pc } = await supabase
        .from('play_credits')
        .select('merged_into, attached_email_verified')
        .eq('player_id', session.session.user.id)
        .maybeSingle();
      if (pc?.merged_into) {
        return { needed: true, hasEmail: false };
      }
    } catch {
      // RLS / offline — fall through; reactive recovery still fires on
      // consume-credit returning 409.
    }
  }

  return { needed: false, hasEmail: false };
}

/**
 * Initialize anonymous auth session.
 * If no existing session is found, checks for recovery state first.
 * Only creates a new anonymous identity when no prior session cookie exists.
 * On timeout (5s), returns silently — caller should route to Practice mode.
 *
 * For fresh anonymous signups, runs the PoW challenge flow first
 * (AUTH_PAYMENT_HARDENING_PLAN.md §6.2.b + §9.2): issue → solve → verify →
 * signup → bind. BroadcastChannel + localStorage leader election protects
 * against the two-tab race per §6.2.b M-AUTH-12.
 */
export async function initAuth(): Promise<RecoveryState> {
  // T-29 / §6.5 — register the visibilitychange refresh listener exactly
  // once. Safe to call on every `initAuth` entry (module-level flag guard).
  installVisibilityRefreshListener();

  // An explicit recovery deep link (emailed /recover?t=… magic link, or
  // Settings → Restore purchases' /?recover=1) must open RecoveryFlow even
  // when this browser already holds a session — on the web it almost always
  // does (anonymous sign-in), so returning early here made both links no-ops.
  const deepLink = hasRecoveryDeepLink();

  const { data: session } = await supabase.auth.getSession();
  if (session?.session) {
    // getSession() returns the cached session even if the access token is
    // expired. Proactively refresh so every subsequent API call has a valid
    // token — otherwise the user can start a game before auto-refresh fires.
    const { error } = await supabase.auth.refreshSession();
    if (!error) return { needed: deepLink, hasEmail: false };
    // Refresh failed (e.g. refresh token also expired). Sign out to clear
    // the stale session from the client's cache — otherwise getSession()
    // keeps returning it and signInAnonymously() no-ops.
    await supabase.auth.signOut();
  }

  // Check if a prior session cookie exists — do NOT silently create new identity.
  const recovery = await checkRecoveryNeeded();
  // The player asked for recovery explicitly — show the flow rather than
  // silently re-minting the cookie's identity (which would swallow the link).
  if (recovery.needed && deepLink) return recovery;
  if (recovery.needed) {
    // V3 §5.1 silent recovery: if the rc_session cookie carries a real
    // (non-provisional) uid AND the uid exists + is not merged/frozen,
    // POST /recovery/silent to mint a fresh session server-side without
    // email / PoW / Turnstile. HMAC signature IS the proof-of-trust.
    try {
      const { silentRecovery } = await import('./recovery-client');
      const res = await silentRecovery();
      if (res.ok && res.access_token && res.refresh_token) {
        await supabase.auth.setSession({
          access_token: res.access_token, refresh_token: res.refresh_token,
        });
        // §4.5 — welcome-back toast. Fire only when the cookie is old
        // enough that the user genuinely "came back" (threshold: 30 d).
        // Sub-threshold recoveries are treated as normal session refresh
        // and stay silent.
        const ageDays = typeof res.recovered_age_days === 'number' ? res.recovered_age_days : 0;
        if (ageDays > 30 && typeof window !== 'undefined') {
          try {
            window.dispatchEvent(new CustomEvent('rc-welcome-back', {
              detail: {
                ageDays,
                hasPaidCredits: !!res.has_paid_credits,
              },
            }));
          } catch {
            // CustomEvent constructor unavailable (ancient browser) —
            // swallow; toast is nice-to-have, not required for recovery.
          }
        }
        return { needed: false, hasEmail: false };
      }
    } catch {
      // Network / import failure — fall through to the reactive RecoveryFlow.
    }
    return recovery;
  }

  // Two-tab race guard (§6.2.b M-AUTH-12). BroadcastChannel elects ONE tab
  // to run the PoW + signup; other tabs wait up to 8 s then re-check for the
  // session the winner wrote. `localStorage` holds the lock timestamp so
  // tabs opened while the leader is mid-solve see the in-flight marker.
  const LOCK_KEY = 'rc_signup_inflight_v1';
  const bc =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('rc_signup') : null;
  try {
    if (typeof localStorage !== 'undefined') {
      const prev = localStorage.getItem(LOCK_KEY);
      const now = Date.now();
      if (prev && now - Number(prev) < 8_000) {
        // Another tab claims the lock; wait for it to announce completion.
        const done = await new Promise<boolean>((res) => {
          const t = setTimeout(() => res(false), 8_000);
          bc?.addEventListener(
            'message',
            (ev: MessageEvent) => {
              if ((ev.data as { type?: string })?.type === 'rc_signup_done') {
                clearTimeout(t);
                res(true);
              }
            },
            { once: true },
          );
        });
        if (done) {
          const { data: s } = await supabase.auth.getSession();
          if (s?.session) {
            bc?.close();
            return { needed: false, hasEmail: false };
          }
        }
      }
      localStorage.setItem(LOCK_KEY, String(now));
    }
  } catch {
    // Storage quota / privacy mode — proceed as winner.
  }

  // First-time sign-in path — run PoW challenge first.
  const deviceHint = await getDeviceHint();
  let allowedToken: string | null = null;
  try {
    allowedToken = await runSignupChallenge(deviceHint);
  } catch (e) {
    // PoW infra down or blocked — fall back to practice mode on caller.
    console.warn('anon-signup-challenge failed:', e);
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      /* private mode */
    }
    bc?.close();
    return { needed: false, hasEmail: false };
  }

  if (!allowedToken) {
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      /* private mode */
    }
    bc?.close();
    return { needed: false, hasEmail: false };
  }

  // Request-scoped custom fetch — no global state mutation (§6.2.b MIN-CLEAN-12).
  const signupClient = makeSignupClient(allowedToken);

  type SignupResult = Awaited<ReturnType<typeof signupClient.auth.signInAnonymously>>;
  const result = (await Promise.race([
    signupClient.auth.signInAnonymously(),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
  ]).catch(() => null)) as SignupResult | null;

  if (!result?.data?.session) {
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      /* private mode */
    }
    bc?.close();
    return { needed: false, hasEmail: false };
  }

  // Hydrate the shared client with the new session so downstream code
  // uses localStorage-persisted session as before.
  await supabase.auth.setSession({
    access_token: result.data.session.access_token,
    refresh_token: result.data.session.refresh_token,
  });

  // Persist the bind intent (§9.2 MIN-CLEAN-20) BEFORE the fire-and-forget
  // POST so the retry-on-next-load path has a record even if the network
  // request is aborted by tab close.
  try {
    sessionStorage.setItem(
      'rc_pending_bind',
      JSON.stringify({
        nonce: _lastSignupNonce,
        player_id: result.data.session.user.id,
        ts: Date.now(),
      }),
    );
  } catch {
    /* quota / private mode; acceptable */
  }

  // Fire-and-forget bind so `player_id` is associated with the anon_signups
  // row and `play_credits.first_seen_*` is populated (§9.2).
  void fetch(`${SUPABASE_URL}/functions/v1/anon-signup-challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${result.data.session.access_token}`,
    },
    body: JSON.stringify({ action: 'bind', nonce: _lastSignupNonce }),
  })
    .then((r) => {
      if (r.ok) {
        try {
          sessionStorage.removeItem('rc_pending_bind');
        } catch {
          /* private mode */
        }
      }
    })
    .catch(() => {
      /* non-blocking; retryPendingBind will pick it up */
    });

  // Release the two-tab lock + broadcast completion (§6.2.b M-AUTH-12).
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {
    /* private mode */
  }
  bc?.postMessage({ type: 'rc_signup_done' });
  bc?.close();

  // Opportunistically flush any older pending bind (e.g. from a prior load
  // whose tab closed mid-bind).
  void retryPendingBind();

  return { needed: false, hasEmail: false };
}

/**
 * PoW challenge: issue → solve → verify. Returns `allowed_token` on success,
 * null on rate-limit / solver timeout / verify rejection. Stores the issued
 * nonce in `_lastSignupNonce` for the subsequent bind POST.
 *
 * Fetches target the direct Supabase functions URL (§7.4.a: only
 * `/auth/v1/*` routes through Vercel; `functions/v1/*` stays direct).
 */
async function runSignupChallenge(deviceHint: string): Promise<string | null> {
  // §3.2 — Turnstile token attached to the `issue` POST. Discriminated
  // result so we can branch on:
  //   token    → send as-is (normal path, ~500 ms invisible challenge).
  //   stub     → local dev / no VITE_TURNSTILE_SITE_KEY → server bypasses
  //              iff TURNSTILE_SECRET_KEY is also unset.
  //   outage   → CF unreachable (ad-block / widget threw / timed out). Send
  //              the outage sentinel; server short-circuits to fail-open
  //              PoW@POW_FALLBACK_FLOOR so legit users aren't locked out.
  //   rejected → CF scored us as a bot on invisible. Retry ONCE with
  //              mode='interactive' (visible widget). If interactive also
  //              rejects, return null → caller drops to practice mode.
  let tokenResult = await getTurnstileToken('anon-signup', 'invisible');

  if (tokenResult.kind === 'rejected') {
    console.log(JSON.stringify({
      metric: 'auth.captcha.interactive_retry',
      error_codes: tokenResult.errorCodes,
      ts: Date.now(),
    }));
    tokenResult = await getTurnstileToken('anon-signup', 'interactive');
  }

  let captchaToken: string;
  switch (tokenResult.kind) {
    case 'token':
      captchaToken = tokenResult.token;
      break;
    case 'stub':
      captchaToken = TURNSTILE_STUB_TOKEN;
      break;
    case 'outage':
      console.log(JSON.stringify({
        metric: 'auth.captcha.client_outage',
        reason: tokenResult.reason,
        ts: Date.now(),
      }));
      captchaToken = TURNSTILE_OUTAGE_TOKEN;
      break;
    case 'rejected':
      // Interactive retry ALSO rejected — treat as terminal. Server will
      // 400 on empty captcha_token; caller drops to practice mode.
      console.log(JSON.stringify({
        metric: 'auth.captcha.interactive_retry_failed',
        error_codes: tokenResult.errorCodes,
        ts: Date.now(),
      }));
      captchaToken = '';
      break;
  }

  // Step 1: issue.
  const issueRes = await fetch(`${SUPABASE_URL}/functions/v1/anon-signup-challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-rc-device-hint': deviceHint,
    },
    body: JSON.stringify({ action: 'issue', captcha_token: captchaToken }),
  });
  if (issueRes.status === 429) return null;
  const issue = (await issueRes.json()) as {
    nonce?: string;
    difficulty?: number;
    expires_at?: string;
  };
  const { nonce, difficulty } = issue;
  if (!nonce || typeof difficulty !== 'number') return null;

  // Persist nonce for the subsequent bind call (§9.2).
  _lastSignupNonce = nonce;

  // Step 2: solve. Worker preferred (§6.2.b resolves M-CLEAN-6). Main-thread
  // fallback for environments without Workers (tests / SSR).
  const solution = await solvePoW(nonce, difficulty, 10_000);
  if (!solution) return null;

  // Step 3: verify. The device-hint header is length-checked by the Vercel
  // edge middleware at 512 bytes (§6.2.b MIN-AUTH-11).
  const vRes = await fetch(`${SUPABASE_URL}/functions/v1/anon-signup-challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-rc-device-hint': deviceHint,
    },
    body: JSON.stringify({ action: 'verify', nonce, solution }),
  });
  if (!vRes.ok) return null;
  const verifyBody = (await vRes.json()) as { allowed_token?: string };
  return verifyBody.allowed_token ?? null;
}

/**
 * Run the PoW solver. Prefers the T-27 shared worker (byte-for-byte parity
 * with the main-thread fallback). Falls back to main-thread `solveBits` in
 * environments without Workers (unit tests / SSR / legacy browsers).
 */
async function solvePoW(
  nonce: string,
  difficulty: number,
  maxMs: number,
): Promise<string | null> {
  if (typeof Worker !== 'undefined') {
    try {
      const worker = new Worker(new URL('./pow-solver.worker.ts', import.meta.url), {
        type: 'module',
      });
      return await new Promise<string | null>((resolve) => {
        const timer = setTimeout(() => {
          worker.terminate();
          resolve(null);
        }, maxMs + 500);
        worker.onmessage = (ev: MessageEvent) => {
          clearTimeout(timer);
          worker.terminate();
          const data = ev.data as { solution?: string | null };
          resolve(data?.solution ?? null);
        };
        worker.onerror = () => {
          clearTimeout(timer);
          worker.terminate();
          resolve(null);
        };
        worker.postMessage({ nonce, difficulty, maxMs });
      });
    } catch {
      // Worker construction failed (e.g. Vite dev-mode quirk in SSR) —
      // fall through to main-thread solve so the signup still completes.
    }
  }
  return solveBits(nonce, difficulty, maxMs);
}

/**
 * Build a request-scoped Supabase client that attaches the PoW-issued
 * `x-rc-anon-signup-token` header to every request. Used only for the
 * `signInAnonymously` call; afterwards the shared `supabase` client is
 * hydrated via `setSession` (§6.2.b MIN-CLEAN-12: no global fetch
 * monkey-patch, no test-order coupling).
 *
 * The returned client ALSO inherits the §7.4.a auth URL rewrite (it builds
 * its own `fetch` wrapper that composes the rewrite with the header add),
 * so `signInAnonymously` hits Vercel, not Supabase directly.
 */
export function makeSignupClient(allowedToken: string): SupabaseLite {
  const baseRewrite = makeAuthRewriteFetch();
  const signupFetch: typeof fetch = (input, init = {}) => {
    const headers = new Headers(init?.headers ?? {});
    headers.set('x-rc-anon-signup-token', allowedToken);
    return baseRewrite(input, { ...init, headers });
  };
  return buildSupabaseLite(signupFetch);
}

// ---------------------------------------------------------------------------
// Bind retry path (§9.2 MIN-CLEAN-20 + MIN-ARCH-17 + MIN-AUTH-21).
//
// When `initAuth` resolves, a fire-and-forget POST carries the nonce to the
// `bind` action — but that POST can be cancelled by tab-close mid-flight,
// by a transient network error, or by Supabase being briefly unavailable.
// To cover those cases without ever double-binding on 10+ open tabs:
//
//   1. The in-flight POST writes `rc_pending_bind` to sessionStorage before
//      firing; it clears the flag only on 2xx.
//   2. A retry path (`retryPendingBind`) checks the flag on every `initAuth`
//      entry and flushes if present.
//   3. Leader election ensures only ONE tab retries, even with 11+ tabs in
//      a background/foreground shuffle:
//       - Primary: BroadcastChannel claim message; the lowest-timestamp
//         claim wins a 50ms arbitration window.
//       - Fallback (Safari < iOS 15.4, some WebViews): localStorage lock
//         with a 5 s TTL, observed via the cross-tab `storage` event.
// ---------------------------------------------------------------------------

const BIND_BC_NAME = 'rc_bind';
const LS_BIND_LOCK_KEY = 'rc_bind_lock_v1';

const bindBc: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(BIND_BC_NAME) : null;
let _bindInFlight = false;

function tryAcquireLsLock(ttlMs = 5000): boolean {
  if (typeof localStorage === 'undefined') return true; // private mode; best-effort.
  const now = Date.now();
  try {
    const raw = localStorage.getItem(LS_BIND_LOCK_KEY);
    if (raw) {
      const existing = JSON.parse(raw) as { until?: number };
      if (typeof existing.until === 'number' && existing.until > now) return false;
    }
    localStorage.setItem(LS_BIND_LOCK_KEY, JSON.stringify({ until: now + ttlMs }));
    return true;
  } catch {
    return true;
  }
}

function releaseLsLock(): void {
  try {
    localStorage.removeItem(LS_BIND_LOCK_KEY);
  } catch {
    /* private mode */
  }
}

/**
 * Flush a pending bind from sessionStorage, with leader election so
 * concurrent tabs do not all race to hit the per-user `rl:bind:<uid>`
 * 10/min limiter (§9.2 MIN-ARCH-17 + MIN-AUTH-21).
 */
export async function retryPendingBind(): Promise<void> {
  if (typeof sessionStorage === 'undefined') return;
  const raw = sessionStorage.getItem('rc_pending_bind');
  if (!raw) return;
  if (_bindInFlight) return;
  _bindInFlight = true;

  try {
    let payload: { nonce?: string | null; player_id?: string; ts?: number };
    try {
      payload = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem('rc_pending_bind');
      return;
    }
    if (!payload.nonce) {
      sessionStorage.removeItem('rc_pending_bind');
      return;
    }

    // Gate 1: BroadcastChannel claim with a 50 ms arbitration window.
    if (bindBc) {
      const claim = crypto.randomUUID();
      bindBc.postMessage({ type: 'bind_claim', id: claim, ts: Date.now() });
      const contested = await new Promise<boolean>((resolve) => {
        const onMsg = (ev: MessageEvent) => {
          const data = ev.data as { type?: string; id?: string; ts?: number };
          if (
            data?.type === 'bind_claim' &&
            data.id !== claim &&
            typeof data.ts === 'number' &&
            data.ts < Date.now()
          ) {
            resolve(true);
          }
        };
        bindBc.addEventListener('message', onMsg);
        setTimeout(() => {
          bindBc.removeEventListener('message', onMsg);
          resolve(false);
        }, 50);
      });
      if (contested) return;
    } else {
      // Gate 2: localStorage fallback lock for environments without BC.
      if (!tryAcquireLsLock(5000)) return;
    }

    const { data: session } = await supabase.auth.getSession();
    const accessToken = session?.session?.access_token;
    if (!accessToken) return; // user no longer logged in; leave flag for next time.

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/anon-signup-challenge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ action: 'bind', nonce: payload.nonce }),
      });
      if (res.ok) {
        sessionStorage.removeItem('rc_pending_bind');
        bindBc?.postMessage({ type: 'bind_done' });
      }
    } catch {
      // Transient; leave the flag and let the next load retry.
    } finally {
      if (!bindBc) releaseLsLock();
    }
  } finally {
    _bindInFlight = false;
  }
}

// Cross-tab signals: when another tab announces bind_done, drop our flag so
// we don't re-run the retry on next focus.
bindBc?.addEventListener('message', (ev: MessageEvent) => {
  const data = ev.data as { type?: string };
  if (data?.type === 'bind_done') {
    try {
      sessionStorage.removeItem('rc_pending_bind');
    } catch {
      /* private mode */
    }
  }
});

// No-BroadcastChannel fallback: observe the cross-tab `storage` event to
// notice a peer acquiring the localStorage lock and clear our pending flag
// (the peer will flush the bind on our behalf).
if (!bindBc && typeof window !== 'undefined') {
  window.addEventListener('storage', (ev: StorageEvent) => {
    if (ev.key !== LS_BIND_LOCK_KEY || !ev.newValue) return;
    try {
      const { until } = JSON.parse(ev.newValue) as { until?: number };
      if (typeof until === 'number' && until > Date.now()) {
        sessionStorage.removeItem('rc_pending_bind');
      }
    } catch {
      /* ignore */
    }
  });
}
